/**
 * The demo's lifecycle, leakage and orders answers (wave 18 stage B), as the API's wire
 * bodies, so the demo client maps them through the same `api/revenueLeakage.ts` mappers
 * the HTTP client does.
 *
 * Built from the demo engine's days (`engine.ts`), as every other demo card is: a
 * competition's curve is its own sales by day, refunds, discounts and failed payments are
 * fixed shares of each day's revenue, and the order list is the days' orders. Every figure
 * is a pure function of (tenant, filters, clock), so it is stable across screens, reloads
 * and test runs.
 */

import type { CsvFile } from '@/api/list'
import type {
  LeakageQuery,
  LifecycleQuery,
  OrderChannel,
  OrderCustomerType,
  OrderRowOut,
  OrdersQuery,
  RevenueLeakageMetaOut,
  RevenueLeakageOut,
  RevenueLifecycleOut,
  RevenueOrdersOut,
} from '@/api/revenueLeakage'
import { ORDER_CHANNELS, ORDERS_CSV_MAX_ROWS, ORDERS_PAGE_SIZE, ordersCsvFilename } from '@/api/revenueLeakage'
import { majorUnits, minorUnitExponent } from '@/design/format'
import { addDays, dayCount, eachDay, resolvePeriod, toDateKey, type DateWindow, type GlobalFilters } from '@/lib/filters'

import { unitNoise, wobble } from '../random'
import type { TenantFixture } from '../tenants/types'
import { DEMO_TIMEZONE, DemoDataset, scopeFor } from './engine'

/** The demo's refund reasons and their shares of refunded money (the rest has none recorded). */
const REFUND_REASONS: readonly (readonly [string, number])[] = [
  ['fulfilment', 0.38],
  ['customer_request', 0.27],
  ['duplicate', 0.14],
  ['unknown', 0.21],
]
/** What share of a day's gross is refunded, discounted, or fails at the gateway, before day-to-day noise. */
const REFUND_SHARE = 0.022
const DISCOUNT_SHARE = 0.041
const FAILED_SHARE = 0.063
const RECOVERED_SHARE = 0.54
const FAILURES: readonly (readonly [string, string, number])[] = [
  ['cashflows', 'declined', 0.41],
  ['cashflows', '3ds', 0.19],
  ['stripe', 'declined', 0.16],
  ['cashflows', 'timeout', 0.11],
  ['stripe', 'cancelled', 0.08],
  ['unknown', 'other', 0.05],
]
const PROVIDERS = ['cashflows', 'cashflows', 'cashflows', 'stripe'] as const

/** Whole days from `from` to `to` (tenant date keys), negative when `to` is earlier. */
function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000)
}

function unit(currency: string): number {
  return 10 ** minorUnitExponent(currency)
}

function meta(tenant: TenantFixture, window: DateWindow, previous: DateWindow | null, basis: 'gross' | 'net', compare: RevenueLeakageMetaOut['compare']): RevenueLeakageMetaOut {
  return {
    currency: tenant.currency,
    timezone: DEMO_TIMEZONE,
    range: { from: window.from, to: window.to },
    previous_range: previous ? { from: previous.from, to: previous.to } : null,
    previous_unavailable: previous ? null : 'not_compared',
    freshness: { latest_source_updated_at: null },
    source: 'live',
    environments: [],
    non_production: false,
    website_id: null,
    basis,
    compare,
  }
}

/** The fixture's competition as the demo's competitions list shows it (`features/analytics/competitions/mock.ts`). */
function dates(dataset: DemoDataset, id: string) {
  const competition = dataset.tenant.competitions.find((candidate) => candidate.id === id)
  if (!competition) return null
  const { opens, closes } = dataset.schedule(competition)
  return { competition, opens, drawDay: addDays(closes, 1), startAt: `${opens}T09:00:00Z`, drawAt: `${addDays(closes, 1)}T19:00:00Z` }
}

// --- lifecycle --------------------------------------------------------------------------------

export function demoLifecycle(tenant: TenantFixture, now: Date, query: LifecycleQuery): RevenueLifecycleOut {
  const dataset = new DemoDataset(tenant, now)
  const scope = scopeFor(tenant, query.filters)
  const minor = unit(tenant.currency)
  const today = toDateKey(now)
  const asked = [...new Set(query.competitionIds)].slice(0, 6)
  const competitions: RevenueLifecycleOut['data']['competitions'] = []
  const notAvailable: RevenueLifecycleOut['data']['not_available'] = []
  for (const id of asked) {
    const found = dates(dataset, id)
    if (!found || found.opens > today) {
      notAvailable.push({ competition_id: id, reason: 'not_found' })
      continue
    }
    const { competition, opens, drawDay } = found
    const last = drawDay < today ? drawDay : today
    const zero = query.align === 'launch' ? opens : drawDay
    let cumulativeRevenue = 0
    let cumulativeTickets = 0
    let soldOut: number | null = null
    const points = eachDay({ from: opens, to: last, days: dayCount(opens, last) }).map((date) => {
      const sold = dataset.day(date, scope).competitions.get(id)
      const gross = Math.round((sold?.revenue ?? 0) * minor)
      const revenue = query.basis === 'gross' ? gross : gross - Math.round(gross * REFUND_SHARE)
      const tickets = Math.round(sold?.tickets ?? 0)
      cumulativeRevenue += revenue
      cumulativeTickets += tickets
      const day = daysBetween(zero, date)
      if (soldOut === null && cumulativeTickets >= competition.maxEntries) soldOut = day
      return {
        day,
        date,
        revenue,
        tickets,
        cumulative_revenue: cumulativeRevenue,
        cumulative_sold_fraction: Math.round((cumulativeTickets / competition.maxEntries) * 10_000) / 10_000,
      }
    })
    competitions.push({
      competition_id: id,
      name: competition.name,
      category: competition.category,
      start_at: found.startAt,
      draw_at: found.drawAt,
      draw_source: 'draw_at',
      capacity: competition.maxEntries,
      sold_out_day: soldOut,
      points,
      truncated: false,
      totals: { revenue: cumulativeRevenue, tickets: cumulativeTickets },
    })
  }
  const period = resolvePeriod(query.filters, now)
  return {
    data: { basis: query.basis, align: query.align, competitions, not_available: notAvailable, clipped_by: 'competition_dates' },
    meta: meta(tenant, period.current, null, query.basis, 'none'),
  }
}

// --- leakage ------------------------------------------------------------------------------------

interface DayLeak {
  date: string
  gross: number
  refunds: number
  refundCount: number
  discounts: number
  failed: number
}

function leaks(dataset: DemoDataset, filters: GlobalFilters, window: DateWindow, minor: number): DayLeak[] {
  const scope = scopeFor(dataset.tenant, filters)
  const seed = dataset.tenant.seed
  return eachDay(window).map((date) => {
    const gross = Math.round(dataset.day(date, scope).revenue * minor)
    const refunds = Math.round(gross * REFUND_SHARE * wobble(seed, 0.9, 'refund', date))
    return {
      date,
      gross,
      refunds,
      refundCount: refunds > 0 ? Math.max(1, Math.round(refunds / (12 * minor))) : 0,
      discounts: Math.round(gross * DISCOUNT_SHARE * wobble(seed, 0.3, 'discount', date)),
      failed: Math.round(gross * FAILED_SHARE * wobble(seed, 0.5, 'failed', date)),
    }
  })
}

/** `total` shared over `shares` (fractions adding to 1) in whole units that add up to it exactly. */
function shareOut(total: number, shares: readonly number[]): number[] {
  const floors = shares.map((share) => Math.floor(total * share))
  let left = total - floors.reduce((sum, value) => sum + value, 0)
  for (let index = 0; left > 0; index = (index + 1) % floors.length, left -= 1) floors[index] = (floors[index] ?? 0) + 1
  return floors
}

const ratio = (value: number, total: number): number | null => (total > 0 ? Math.round((value / total) * 10_000) / 10_000 : null)

export function demoLeakage(tenant: TenantFixture, now: Date, query: LeakageQuery): RevenueLeakageOut {
  const dataset = new DemoDataset(tenant, now)
  const minor = unit(tenant.currency)
  const period = resolvePeriod(query.filters, now)
  const compare: RevenueLeakageMetaOut['compare'] =
    query.filters.compare === 'none' ? 'none' : query.filters.compare === 'previous_year' ? 'same_period_last_year' : 'previous_period'
  const previousWindow = compare === 'none' ? null : period.previous
  const days = leaks(dataset, query.filters, period.current, minor)
  const before = previousWindow ? leaks(dataset, query.filters, previousWindow, minor) : null
  const sum = (rows: readonly DayLeak[], pick: (row: DayLeak) => number) => rows.reduce((total, row) => total + pick(row), 0)
  const gross = sum(days, (day) => day.gross)
  const refunds = sum(days, (day) => day.refunds)
  const refundCount = sum(days, (day) => day.refundCount)
  const reasonAmounts = shareOut(refunds, REFUND_REASONS.map(([, share]) => share))
  const reasonCounts = shareOut(refundCount, REFUND_REASONS.map(([, share]) => share))
  const failed = sum(days, (day) => day.failed)
  const failedAmounts = shareOut(failed, FAILURES.map(([, , share]) => share))
  const recoveredAmounts = failedAmounts.map((value, index) => Math.round(value * RECOVERED_SHARE * wobble(tenant.seed, 0.2, 'recovered', index)))
  const recovered = recoveredAmounts.reduce((total, value) => total + value, 0)
  const attempts = Math.round(failed / (14 * minor))
  const attemptCounts = shareOut(attempts, FAILURES.map(([, , share]) => share))
  const draws = tenant.competitions
    .map((competition) => dates(dataset, competition.id))
    .filter((found): found is NonNullable<typeof found> => found !== null && found.drawDay >= period.current.from && found.drawDay <= period.current.to)
    .sort((a, b) => a.drawAt.localeCompare(b.drawAt))
  const discounts = sum(days, (day) => day.discounts)
  return {
    data: {
      basis: query.basis,
      compare,
      revenue_gross: gross,
      refunds: {
        amount: refunds,
        count: refundCount,
        rate: ratio(refunds, gross),
        rate_null_reason: gross > 0 ? null : 'no_revenue',
        median_lag_days: refundCount >= 5 ? 3.4 : null,
        median_lag_null_reason: refundCount >= 5 ? null : 'too_few_refunds',
        lag_refunds: refundCount,
        by_reason: REFUND_REASONS.map(([reason], index) => ({ reason_category: reason, amount: reasonAmounts[index] ?? 0, count: reasonCounts[index] ?? 0 })).filter(
          (row) => row.amount > 0 || row.count > 0,
        ),
        by_day: days.map((day) => ({ day: day.date, amount: day.refunds, count: day.refundCount })),
        previous_amount: before ? sum(before, (day) => day.refunds) : null,
        previous_rate: before ? ratio(sum(before, (day) => day.refunds), sum(before, (day) => day.gross)) : null,
        previous_rate_null_reason: before && sum(before, (day) => day.gross) <= 0 ? 'no_revenue' : null,
      },
      discounts: {
        amount: discounts,
        share_of_gross: ratio(discounts, gross),
        share_null_reason: gross > 0 ? null : 'no_revenue',
        orders_with_discount: Math.round(discounts / (6 * minor)),
        by_coupon: null,
        by_coupon_null_reason: 'no_coupon_source',
        previous_amount: before ? sum(before, (day) => day.discounts) : null,
      },
      failed_payments: {
        value: failed,
        orders: Math.round(attempts * 0.7),
        attempts,
        attempts_without_value: 0,
        recovered_value: recovered,
        unrecovered_value: failed - recovered,
        recovered_rate: ratio(recovered, failed),
        recovered_rate_null_reason: failed > 0 ? null : 'no_failures',
        share_of_gross: ratio(failed, gross),
        share_null_reason: gross > 0 ? null : 'no_revenue',
        by_provider: FAILURES.map(([provider, kind], index) => ({
          provider,
          failure_kind: kind,
          value: failedAmounts[index] ?? 0,
          recovered_value: recoveredAmounts[index] ?? 0,
          attempts: attemptCounts[index] ?? 0,
        })),
      },
      failed_payments_null_reason: null,
      draws: draws.map((found) => ({ type: 'draw', at: found.drawAt, source: 'draw_at', competition_id: found.competition.id, name: found.competition.name })),
    },
    meta: meta(tenant, period.current, previousWindow, query.basis, compare),
  }
}

// --- orders ---------------------------------------------------------------------------------

/** The last list built, by tenant, day and filters: paging asks for the same list again and again. */
let built: { key: string; rows: OrderRowOut[] } | null = null

/** Every demo order in the query's period and scope: the days' orders, each with a competition. */
function demoOrderRows(tenant: TenantFixture, now: Date, filters: GlobalFilters): OrderRowOut[] {
  const key = [tenant.id, toDateKey(now), JSON.stringify(filters)].join('|')
  if (built?.key !== key) built = { key, rows: buildOrderRows(tenant, now, filters) }
  return built.rows
}

function buildOrderRows(tenant: TenantFixture, now: Date, filters: GlobalFilters): OrderRowOut[] {
  const dataset = new DemoDataset(tenant, now)
  const scope = scopeFor(tenant, filters)
  const minor = unit(tenant.currency)
  const { current } = resolvePeriod(filters, now)
  const rows: OrderRowOut[] = []
  for (const date of eachDay(current)) {
    const day = dataset.day(date, scope)
    const sold = [...day.competitions.entries()].filter(([, totals]) => totals.orders > 0)
    sold.forEach(([competitionId, totals]) => {
      const competition = tenant.competitions.find((candidate) => candidate.id === competitionId)
      const count = Math.round(totals.orders)
      for (let index = 0; index < count; index += 1) {
        const key = `${date}|${competitionId}|${index}`
        const noise = unitNoise(tenant.seed, 'order', key)
        const tickets = 1 + Math.floor(noise * 6)
        const gross = Math.round((competition?.ticketPrice ?? 1) * tickets * minor)
        const refunded = noise > 0.97 ? gross : 0
        const hour = 7 + Math.floor(unitNoise(tenant.seed, 'hour', key) * 16)
        const channel = ORDER_CHANNELS[Math.floor(unitNoise(tenant.seed, 'channel', key) * ORDER_CHANNELS.length)] as OrderChannel
        const typeNoise = unitNoise(tenant.seed, 'type', key)
        const customerType: OrderCustomerType = typeNoise < 0.12 ? 'guest' : typeNoise < 0.4 ? 'new' : 'returning'
        const number = 40_000 + rows.length
        rows.push({
          order_id: `ord_demo_${date.replaceAll('-', '')}_${competitionId}_${index}`,
          external_order_id: String(number),
          completed_at: `${date}T${String(hour).padStart(2, '0')}:${String(index % 60).padStart(2, '0')}:00Z`,
          competitions: competition ? [{ competition_id: competition.id, name: competition.name }] : [],
          unsynced_competition_lines: competition ? 0 : 1,
          tickets,
          currency: tenant.currency,
          gross,
          net: gross - (noise > 0.85 ? Math.round(gross * 0.1) : 0),
          refunded,
          payment_provider: PROVIDERS[number % PROVIDERS.length] ?? null,
          payment_method: 'card',
          channel,
          customer_type: customerType,
          customer_id: customerType === 'guest' ? null : `cus_demo_${Math.floor(noise * 5000)}`,
        })
      }
    })
  }
  return rows
}

function compareRows(sort: OrdersQuery['sort'], order: OrdersQuery['order']) {
  const value = (row: OrderRowOut): string | number => (sort === 'completed_at' ? row.completed_at : row[sort])
  return (a: OrderRowOut, b: OrderRowOut) => {
    const left = value(a)
    const right = value(b)
    const byValue = left < right ? -1 : left > right ? 1 : a.order_id.localeCompare(b.order_id)
    return order === 'desc' ? -byValue : byValue
  }
}

/** Every demo order passing the query's filters, in its order. */
export function demoOrdersMatching(tenant: TenantFixture, now: Date, query: OrdersQuery): OrderRowOut[] {
  return [...demoOrderRows(tenant, now, query.filters)]
    .filter((row) => !query.competitionId || row.competitions.some((competition) => competition.competition_id === query.competitionId))
    .filter((row) => !query.channel || row.channel === query.channel)
    .filter((row) => !query.paymentProvider || (row.payment_provider ?? 'unknown') === query.paymentProvider.toLowerCase())
    .filter((row) => !query.customerType || row.customer_type === query.customerType)
    .filter((row) => query.minGrossMinor === undefined || row.gross >= query.minGrossMinor)
    .sort(compareRows(query.sort, query.order))
}

/** One page: the demo's cursor is the offset of the next row. */
export function demoOrders(tenant: TenantFixture, now: Date, query: OrdersQuery): RevenueOrdersOut {
  const matching = demoOrdersMatching(tenant, now, query)
  const offset = query.cursor ? Number.parseInt(query.cursor.replace(/^demo-/, ''), 10) || 0 : 0
  const limit = query.limit ?? ORDERS_PAGE_SIZE
  const rows = matching.slice(offset, offset + limit)
  const next = offset + limit < matching.length ? `demo-${offset + limit}` : null
  const period = resolvePeriod(query.filters, now)
  return {
    data: { rows, total: matching.length, sort: query.sort, order: query.order, model: query.model ?? 'last_non_direct', limit, next_cursor: next },
    meta: meta(tenant, period.current, null, 'gross', 'none'),
  }
}

const CSV_HEADER = [
  'completed_at',
  'order_id',
  'external_order_id',
  'competition_ids',
  'competition_names',
  'unsynced_competition_lines',
  'tickets',
  'currency',
  'gross',
  'net',
  'refunded',
  'payment_provider',
  'payment_method',
  'channel',
  'customer_type',
  'customer_id',
] as const

function csvCell(value: string | number): string {
  const text = String(value)
  return /[",\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text
}

/** The demo's CSV: the API's columns, every matching order up to the API's limit. */
export function demoOrdersCsv(tenant: TenantFixture, now: Date, query: OrdersQuery): CsvFile {
  const matching = demoOrdersMatching(tenant, now, query)
  const money = (minor: number, currency: string) => majorUnits(minor, currency).toFixed(minorUnitExponent(currency))
  const lines = [
    CSV_HEADER.join(','),
    ...matching.slice(0, ORDERS_CSV_MAX_ROWS).map((row) =>
      [
        row.completed_at,
        row.order_id,
        row.external_order_id,
        row.competitions.map((competition) => competition.competition_id).join(' '),
        row.competitions.map((competition) => competition.name).join('; '),
        row.unsynced_competition_lines,
        row.tickets,
        row.currency,
        money(row.gross, row.currency),
        money(row.net, row.currency),
        money(row.refunded, row.currency),
        row.payment_provider ?? '',
        row.payment_method ?? '',
        row.channel,
        row.customer_type,
        row.customer_id ?? '',
      ]
        .map(csvCell)
        .join(','),
    ),
  ]
  const { current } = resolvePeriod(query.filters, now)
  return {
    blob: new Blob([`${lines.join('\n')}\n`], { type: 'text/csv' }),
    filename: ordersCsvFilename(current.from, current.to),
    truncated: matching.length > ORDERS_CSV_MAX_ROWS,
    totalRows: matching.length,
  }
}
